import { Button, Modal, ScrollArea, toast } from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { Loader2Icon, PrinterIcon } from "lucide-react";
import { useMemo } from "react";
import logo from "@/assets/favicon_io/android-chrome-192x192.png";
import { config } from "@/shared/lib/config";
import { formatInLao, formatTime } from "@/shared/lib/date-time";
import {
  formatAssistInstructorBillLine,
  formatBillAmount,
  RECEIPT_LOGO_STYLE_48MM,
  RECEIPT_PRINT_STYLES_48MM,
  uniqueAssistInstructorNames,
} from "@/shared/lib/receipt-print-48mm";
import type { ReceiptDetailDTO, ReceiptItemDTO } from "../api/client";
import { billingApi } from "../api/client";

type MergeRoomBookingReceiptPreviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receiptIds: string[];
};

function getRoomBookerName(receipts: ReceiptDetailDTO[]): string {
  for (const receipt of receipts) {
    for (const item of receipt.items) {
      if (item.itemType === "room_booking" && item.contactName?.trim()) {
        return item.contactName.trim();
      }
    }
  }
  return "-";
}

function collectRoomBookingItems(receipts: ReceiptDetailDTO[]): Array<{
  receipt: ReceiptDetailDTO;
  item: ReceiptItemDTO;
}> {
  const rows: Array<{ receipt: ReceiptDetailDTO; item: ReceiptItemDTO }> = [];
  for (const receipt of receipts) {
    for (const item of receipt.items) {
      if (item.itemType === "room_booking") {
        rows.push({ receipt, item });
      }
    }
  }
  return rows;
}

function escapeBillHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

type MergeSession = {
  date: string;
  day: number;
  startTime: string | null;
  endTime: string | null;
  roomName: string | null;
  amount: number;
};

type MonthBlock = {
  monthKey: string;
  label: string;
  sessions: MergeSession[];
  totalPrice: number;
  assistInstructorNames: string[];
};

type OpaqueBlock = {
  key: string;
  lines: string[];
  totalPrice: number;
  assistInstructorNames: string[];
};

function extractItemLabel(item: ReceiptItemDTO, fallback: string): string {
  const first = item.description.split("\n")[0]?.trim() ?? "";
  const idx = first.indexOf(" — ");
  if (idx >= 0) {
    const left = first.slice(0, idx).trim();
    if (left) return left;
  }
  if (item.contactName?.trim()) return item.contactName.trim();
  return fallback || "ຈອງຫ້ອງ";
}

function parseBulkMonthKey(description: string): string | null {
  const first = description.split("\n")[0]?.trim() ?? "";
  const m = first.match(/—\s*(\d{1,2})\/(\d{4})\s*$/);
  if (!m) return null;
  return `${m[2]}-${m[1].padStart(2, "0")}`;
}

function parseBulkDays(description: string): number[] {
  const dayLine = description
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.startsWith("ວັນ:"));
  if (!dayLine) return [];
  const m = dayLine.match(/ວັນ:\s*([^(]+)/);
  if (!m?.[1]) return [];
  return [
    ...new Set(
      m[1]
        .split(",")
        .map((p) => Number(p.trim()))
        .filter((n) => Number.isFinite(n) && n > 0),
    ),
  ].sort((a, b) => a - b);
}

function parseBulkRoomAndTimes(description: string): {
  rooms: string[];
  times: string[];
} {
  const roomLine = description
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.startsWith("ຫ້ອງ:"));
  if (!roomLine) return { rooms: [], times: [] };
  const body = roomLine.slice("ຫ້ອງ:".length).trim();
  const [roomsPart, timesPart] = body.split(" · ").map((s) => s.trim());
  const rooms = (roomsPart ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const times = (timesPart ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return { rooms, times };
}

function expandItemSessions(
  item: ReceiptItemDTO,
  label: string,
):
  | { kind: "month"; monthKey: string; label: string; sessions: MergeSession[] }
  | { kind: "opaque"; lines: string[]; totalPrice: number } {
  const bulkDays = parseBulkDays(item.description);
  const bulkMonth = parseBulkMonthKey(item.description);

  if (bulkDays.length > 0 && (bulkMonth || item.date)) {
    const monthKey = bulkMonth ?? item.date!.slice(0, 7);
    const { rooms, times } = parseBulkRoomAndTimes(item.description);
    const roomName = rooms[0] ?? item.roomName ?? null;
    let startTime = item.startTime ?? null;
    let endTime = item.endTime ?? null;
    if ((!startTime || !endTime) && times[0]) {
      const [s, e] = times[0].split("-").map((t) => t.trim());
      startTime = s ?? null;
      endTime = e ?? null;
    }
    const qty = Math.max(item.quantity || bulkDays.length, bulkDays.length);
    const unit = item.totalPrice / qty;
    const sessions = bulkDays.map((day) => ({
      date: `${monthKey}-${String(day).padStart(2, "0")}`,
      day,
      startTime,
      endTime,
      roomName,
      amount: unit,
    }));
    return { kind: "month", monthKey, label, sessions };
  }

  if (item.date && item.date.length >= 10) {
    const monthKey = item.date.slice(0, 7);
    const day = Number(item.date.slice(8, 10));
    const qty = Math.max(item.quantity || 1, 1);
    const unit = item.totalPrice / qty;
    const sessions: MergeSession[] = Array.from({ length: qty }, () => ({
      date: item.date!,
      day: Number.isFinite(day) ? day : 0,
      startTime: item.startTime ?? null,
      endTime: item.endTime ?? null,
      roomName: item.roomName ?? null,
      amount: unit,
    }));
    return { kind: "month", monthKey, label, sessions };
  }

  const lines = item.description
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  return {
    kind: "opaque",
    lines: lines.length > 0 ? lines : [label],
    totalPrice: item.totalPrice,
  };
}

function mergeAssistNames(
  target: string[],
  item: ReceiptItemDTO,
): string[] {
  return uniqueAssistInstructorNames([
    ...target,
    ...(item.assistInstructorNames ?? []),
  ]);
}

function buildMonthDescription(
  label: string,
  monthKey: string,
  sessions: MergeSession[],
): string {
  const [year, month] = monthKey.split("-");
  const days = [
    ...new Set(sessions.map((s) => String(s.day)).filter((d) => d !== "0")),
  ].sort((a, b) => Number(a) - Number(b));
  const rooms = [
    ...new Set(
      sessions.map((s) => s.roomName?.trim()).filter(Boolean) as string[],
    ),
  ];
  const times = [
    ...new Set(
      sessions
        .filter((s) => s.startTime && s.endTime)
        .map(
          (s) => `${formatTime(s.startTime!)}-${formatTime(s.endTime!)}`,
        ),
    ),
  ];

  const lines = [
    `${label} — ${month}/${year}`,
    `ວັນ: ${days.join(", ")} (${sessions.length} ຄັ້ງ)`,
  ];
  if (rooms.length > 0) {
    lines.push(
      `ຫ້ອງ: ${rooms.join(", ")}${
        times.length > 0 ? ` · ${times.join(", ")}` : ""
      }`,
    );
  }
  return lines.join("\n");
}

function appendAssistLineToDescription(
  description: string,
  assistInstructorNames: string[],
): string {
  const line = formatAssistInstructorBillLine(assistInstructorNames);
  if (!line) return description;
  return `${description}\n${line}`;
}

function groupMergedRoomBookings(
  rows: Array<{ receipt: ReceiptDetailDTO; item: ReceiptItemDTO }>,
  customerName: string,
): { months: MonthBlock[]; opaque: OpaqueBlock[]; sessionCount: number } {
  const fallbackLabel =
    customerName !== "-" ? customerName : "ຈອງຫ້ອງ";
  // Group by title + month so "Private Emily / Alisa / Jojo" stay separate
  const byLabelMonth = new Map<string, MonthBlock>();
  const opaque: OpaqueBlock[] = [];
  let sessionCount = 0;

  for (const { receipt, item } of rows) {
    const label = extractItemLabel(item, fallbackLabel);
    const expanded = expandItemSessions(item, label);
    if (expanded.kind === "opaque") {
      opaque.push({
        key: `${receipt.id}-${item.id}`,
        lines: expanded.lines,
        totalPrice: expanded.totalPrice,
        assistInstructorNames: uniqueAssistInstructorNames(
          item.assistInstructorNames,
        ),
      });
      sessionCount += Math.max(item.quantity || 1, 1);
      continue;
    }

    const monthTotal = expanded.sessions.reduce((sum, s) => sum + s.amount, 0);
    sessionCount += expanded.sessions.length;
    const groupKey = `${expanded.label}::${expanded.monthKey}`;
    const existing = byLabelMonth.get(groupKey);
    if (existing) {
      existing.sessions.push(...expanded.sessions);
      existing.totalPrice += monthTotal;
      existing.assistInstructorNames = mergeAssistNames(
        existing.assistInstructorNames,
        item,
      );
    } else {
      byLabelMonth.set(groupKey, {
        monthKey: expanded.monthKey,
        label: expanded.label,
        sessions: [...expanded.sessions],
        totalPrice: monthTotal,
        assistInstructorNames: uniqueAssistInstructorNames(
          item.assistInstructorNames,
        ),
      });
    }
  }

  const months = [...byLabelMonth.values()].sort((a, b) => {
    const monthCmp = a.monthKey.localeCompare(b.monthKey);
    if (monthCmp !== 0) return monthCmp;
    return a.label.localeCompare(b.label);
  });
  for (const month of months) {
    month.sessions.sort((a, b) => {
      if (a.day !== b.day) return a.day - b.day;
      return (a.startTime ?? "").localeCompare(b.startTime ?? "");
    });
  }

  return { months, opaque, sessionCount };
}

function buildMergedItemBlocks(
  rows: Array<{ receipt: ReceiptDetailDTO; item: ReceiptItemDTO }>,
  customerName: string,
): string {
  const { months, opaque } = groupMergedRoomBookings(rows, customerName);
  const parts: string[] = [];

  for (const month of months) {
    const description = appendAssistLineToDescription(
      buildMonthDescription(month.label, month.monthKey, month.sessions),
      month.assistInstructorNames,
    );
    const lines = description
      .split("\n")
      .map(
        (line) =>
          `<div class="bill-item-line">${escapeBillHtml(line)}</div>`,
      )
      .join("");
    parts.push(`
      <div class="bill-item-block">
        ${lines}
        <div class="bill-item-amt">${formatBillAmount(Math.round(month.totalPrice))} ກີບ</div>
      </div>
    `);
  }

  for (const block of opaque) {
    const description = appendAssistLineToDescription(
      block.lines.join("\n"),
      block.assistInstructorNames,
    );
    const lines = description
      .split("\n")
      .map(
        (line) =>
          `<div class="bill-item-line">${escapeBillHtml(line)}</div>`,
      )
      .join("");
    parts.push(`
      <div class="bill-item-block">
        ${lines}
        <div class="bill-item-amt">${formatBillAmount(block.totalPrice)} ກີບ</div>
      </div>
    `);
  }

  return parts.join("");
}

function buildMergedRoomBookingContent(
  receipts: ReceiptDetailDTO[],
  logoBase64?: string,
): string {
  const customerName = getRoomBookerName(receipts);
  const issuedAtFormatted = formatInLao(
    receipts[0]?.issuedAt ?? new Date().toISOString(),
    "dd/MM/yyyy HH:mm",
  );
  const rows = collectRoomBookingItems(receipts);
  const grouped = groupMergedRoomBookings(rows, customerName);
  const itemsHtml = buildMergedItemBlocks(rows, customerName);

  const originalSum = receipts.reduce(
    (sum, r) => sum + (r.payment.originalAmount ?? r.payment.amount),
    0,
  );
  const totalAmount = receipts.reduce((sum, r) => sum + r.payment.amount, 0);
  const totalDiscount = receipts.reduce(
    (sum, r) => sum + (r.payment.discountAmount ?? 0),
    0,
  );
  const hasDiscount = totalDiscount > 0 && originalSum > totalAmount;

  const discountHtml = hasDiscount
    ? `
    <div class="bill-discount">
      <div class="bill-discount-line">ຍອດເງິນ: ${formatBillAmount(originalSum)} ກີບ</div>
      <div class="bill-discount-line discount-amount">ສ່ວນຫຼຸດ: -${formatBillAmount(totalDiscount)} ກີບ</div>
    </div>`
    : "";

  const logoImg =
    logoBase64 &&
    `<img src="${logoBase64}" alt="Logo" style="${RECEIPT_LOGO_STYLE_48MM}" />`;

  const tagline = config.billFooterTagline ?? "Keep Dancing, Keep Moving!";
  const address = config.brandAddress ?? "VIENTIANE CENTER, 4th floor";
  const addressLo = config.brandAddressLo ?? "ວຽງຈັນເຊັນເຕີ, ຊັ້ນ4";

  return `
    <div class="bill-header">
      ${logoImg ?? ""}
      <div class="bill-brand">${escapeBillHtml(config.brandName.toUpperCase())}</div>
      <div class="bill-address">${escapeBillHtml(addressLo)}</div>
      <div class="bill-address">${escapeBillHtml(address)}</div>
    </div>
    <hr class="bill-sep" />

    <div class="bill-line">ຊື່: ${escapeBillHtml(customerName)}</div>
    <div class="bill-line">ວັນທີອອກບິນ: ${escapeBillHtml(issuedAtFormatted)}</div>
    <div class="bill-count-line">${grouped.sessionCount} ຄັ້ງ · ${receipts.length} ໃບ</div>
    <hr class="bill-sep" />

    ${itemsHtml}
    <hr class="bill-sep" />
    ${discountHtml}

    <div class="bill-total-section">
      <div class="bill-total-label">ຍອດລວມທັງໝົດ</div>
      <div class="bill-total-amount">${formatBillAmount(totalAmount)} ກີບ</div>
    </div>
    <hr class="bill-sep" />
    <div class="bill-footer">
      <div class="bill-footer-tagline">${escapeBillHtml(tagline)}</div>
      <div class="bill-footer-thanks">ຂອບໃຈທີ່ໃຊ້ບໍລິການ</div>
    </div>
  `;
}

async function loadLogoBase64(): Promise<string> {
  return new Promise<string>((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } else resolve("");
    };
    img.onerror = () => resolve("");
    img.src = logo;
  });
}

export async function printMergedRoomBookingReceipts(
  receipts: ReceiptDetailDTO[],
): Promise<void> {
  const logoBase64 = await loadLogoBase64();
  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  const content = buildMergedRoomBookingContent(
    receipts,
    logoBase64 || undefined,
  );
  const titleNos = receipts.map((r) => r.receiptNo).join("-");

  printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>ໃບເກັບເງິນ - merge ${titleNos}</title>
          <style>${RECEIPT_PRINT_STYLES_48MM}</style>
        </head>
        <body>${content}</body>
      </html>
    `);

  printWindow.document.close();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
}

export function MergeRoomBookingReceiptPreviewDialog({
  open,
  onOpenChange,
  receiptIds,
}: MergeRoomBookingReceiptPreviewDialogProps) {
  const sortedIds = useMemo(
    () => [...new Set(receiptIds)].sort(),
    [receiptIds],
  );

  const { data: receipts, isLoading, isError, error } = useQuery({
    queryKey: ["billing", "receipts", "merge-preview", sortedIds],
    queryFn: async () => {
      const rows = await Promise.all(
        sortedIds.map((id) => billingApi.receipts.getById(id)),
      );
      return rows.sort(
        (a, b) =>
          new Date(a.issuedAt).getTime() - new Date(b.issuedAt).getTime(),
      );
    },
    enabled: open && sortedIds.length > 0,
  });

  const customerName = receipts ? getRoomBookerName(receipts) : "-";
  const itemRows = receipts ? collectRoomBookingItems(receipts) : [];
  const groupedPreview = groupMergedRoomBookings(itemRows, customerName);
  const totalAmount =
    receipts?.reduce((sum, r) => sum + r.payment.amount, 0) ?? 0;
  const originalSum =
    receipts?.reduce(
      (sum, r) => sum + (r.payment.originalAmount ?? r.payment.amount),
      0,
    ) ?? 0;
  const totalDiscount =
    receipts?.reduce((sum, r) => sum + (r.payment.discountAmount ?? 0), 0) ?? 0;
  const hasDiscount = totalDiscount > 0 && originalSum > totalAmount;

  const handlePrint = async () => {
    if (!receipts?.length) return;
    try {
      await printMergedRoomBookingReceipts(receipts);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ພິມບໍ່ສຳເລັດ");
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ພິມລວມໃບຈອງຫ້ອງ"
      size="lg"
    >
      <ScrollArea className="max-h-[80vh] pr-2">
        <div className="space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : isError ? (
            <p className="py-8 text-center text-destructive text-sm">
              {error instanceof Error ? error.message : "ໂຫຼດໃບເກັບເງິນບໍ່ສຳເລັດ"}
            </p>
          ) : !receipts?.length ? (
            <p className="py-8 text-center text-muted-foreground text-sm">
              ບໍ່ມີໃບເກັບເງິນ
            </p>
          ) : (
            <div className="mx-auto max-w-2xl rounded-lg border bg-white p-10 shadow-lg">
              <div className="mb-8 border-red-600 border-b-3 pb-6 text-center">
                <img
                  src={logo}
                  alt="Logo"
                  className="mx-auto mb-4 size-20 rounded-lg object-contain"
                />
                <h1 className="mb-2 font-bold text-3xl text-gray-900">
                  {config.brandName}
                </h1>
                <h2 className="mb-1 font-semibold text-gray-600 text-lg uppercase tracking-wide">
                  ໃບເກັບເງິນ (ລວມ)
                </h2>
                <p className="font-medium text-gray-500 text-sm uppercase tracking-wider">
                  Merged Room Booking Receipt
                </p>
              </div>

              <div className="mb-6 space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">ຊື່ຜູ້ຈອງ</span>
                  <span className="font-semibold">{customerName}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">ສະຫຼຸບ</span>
                  <span className="font-semibold">
                    {groupedPreview.sessionCount} ຄັ້ງ · {receipts.length} ໃບ
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="shrink-0 text-gray-600">ເລກທີ</span>
                  <span className="text-right font-mono text-xs leading-relaxed">
                    {receipts.map((r) => r.receiptNo).join(", ")}
                  </span>
                </div>
              </div>

              <div className="mb-8">
                <p className="mb-4 border-gray-200 border-b-2 pb-2 font-bold text-gray-900 text-sm uppercase tracking-wide">
                  ລາຍການ
                </p>
                <div className="space-y-4">
                  {groupedPreview.months.map((month) => {
                    const description = buildMonthDescription(
                      month.label,
                      month.monthKey,
                      month.sessions,
                    );
                    const lines = description.split("\n").filter(Boolean);
                    return (
                      <div
                        key={`${month.label}::${month.monthKey}`}
                        className="flex justify-between gap-3 border-gray-100 border-b py-3"
                      >
                        <div className="min-w-0 flex-1 space-y-1">
                          {lines.map((line) => (
                            <div
                              key={line}
                              className={
                                line === lines[0]
                                  ? "font-semibold text-gray-900"
                                  : "text-gray-600 text-sm"
                              }
                            >
                              {line}
                            </div>
                          ))}
                        </div>
                        <div className="shrink-0 text-right font-semibold text-gray-900">
                          {Math.round(month.totalPrice).toLocaleString("lo-LA")} 
                          ກີບ
                        </div>
                      </div>
                    );
                  })}
                  {groupedPreview.opaque.map((block) => (
                    <div
                      key={block.key}
                      className="flex justify-between gap-3 border-gray-100 border-b py-3"
                    >
                      <div className="min-w-0 flex-1 space-y-1">
                        {block.lines.map((line) => (
                          <div
                            key={line}
                            className="font-semibold text-gray-900 whitespace-pre-line"
                          >
                            {line}
                          </div>
                        ))}
                      </div>
                      <div className="shrink-0 text-right font-semibold text-gray-900">
                        {block.totalPrice.toLocaleString("lo-LA")} ກີບ
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-red-600 border-t-3 pt-6 text-right">
                <p className="mb-2 font-semibold text-gray-600 text-sm uppercase tracking-wide">
                  ຈຳນວນເງິນທີ່ຕ້ອງຊຳລະທັງໝົດ
                </p>
                {hasDiscount ? (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">ຍອດເງິນ:</span>
                      <span>
                        {originalSum.toLocaleString("lo-LA")} ກີບ
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-600 text-sm">
                      <span>ສ່ວນຫຼຸດ:</span>
                      <span className="font-semibold">
                        -{totalDiscount.toLocaleString("lo-LA")} ກີບ
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-semibold">ລາຄາສຸດທ້າຍ:</span>
                      <span className="font-bold text-4xl text-red-600">
                        {totalAmount.toLocaleString("lo-LA")} ກີບ
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="font-bold text-4xl text-red-600">
                    {totalAmount.toLocaleString("lo-LA")} ກີບ
                  </p>
                )}
              </div>
            </div>
          )}

          {receipts && receipts.length > 0 ? (
            <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-white pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                ຍົກເລີກ
              </Button>
              <Button
                onClick={() => void handlePrint()}
                className="bg-red-600 hover:bg-red-700"
              >
                <PrinterIcon className="mr-2 size-4" />
                ພິມ
              </Button>
            </div>
          ) : null}
        </div>
      </ScrollArea>
    </Modal>
  );
}
